// src/prototype/components/oir/report/OIRReportView.tsx
// ============================================================
// Orchestrator for the Portfolio Strategy Report tab.
// UX Improvements (March 2026):
//   - OIRPageHeader / redundant title band removed entirely
//   - Export + Share buttons live in the EATabBar row (tab-level)
//   - Section order updated: Financial Impact now precedes
//     Strategy Decision Canvas (value first, then selection)
//   - 2-column layout: 240px sticky index rail + content
//   - Section anchor IDs for OIRReportIndex scroll nav
// ============================================================

import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { OIRReportIndex } from './OIRReportIndex';
import { OIRSectionExecutiveSummary } from './OIRSectionExecutiveSummary';
import { OIRSectionStrategicContext } from './OIRSectionStrategicContext';
import { OIRSectionOpportunityLandscape } from './OIRSectionOpportunityLandscape';
import { OIRSectionStrategyDecisionCanvas } from './OIRSectionStrategyDecisionCanvas';
import { OIRSectionFinancialImpact } from './OIRSectionFinancialImpact';
import { OIRSectionExecutionRoadmap } from './OIRSectionExecutionRoadmap';

// ── Types ──────────────────────────────────────────────────────────────────
interface SurfacedReason {
  contextLabel?: string;
  contextValue?: string;
  relevance: string;
}

interface Opportunity {
  id: string;
  rank: number;
  title: string;
  category: string;
  description?: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  timeToValue: string;
  roiEstimate: { low: number; high: number; unit?: string };
  confidence: string | number;
  primaryValueDriver?: string;
  surfacedReasons?: SurfacedReason[];
}

export interface OIRReportViewProps {
  clientName: string;
  rankedOpportunities: Opportunity[];
  selectedOpportunities: Opportunity[];
  top10: Opportunity[];
  portfolioRangeLabel: string;
  shortlistedRangeLabel: string;
  finalistCount: number;
  includedCount: number;
  onOpenExplorationTab: () => void;
  onContinueToExecute: () => void;
  onNavigate: (path: string, id: string) => void;
  onBack: () => void;
}

// OIRPageHeader removed — Export/Share live in the EATabBar row (tab-level actions).
// The redundant title band has been eliminated per UX March 2026 improvements.

// ── Section Anchor Wrapper ─────────────────────────────────────────────────
// Adds an id + scroll-margin so the sticky header doesn't obscure the target
const Section: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <div id={id} style={{ scrollMarginTop: '88px' }}>
    {children}
  </div>
);

// ── Component ──────────────────────────────────────────────────────────────
export const OIRReportView: React.FC<OIRReportViewProps> = ({
  clientName,
  rankedOpportunities,
  selectedOpportunities,
  top10,
  portfolioRangeLabel,
  shortlistedRangeLabel,
  finalistCount,
  includedCount,
  onNavigate,
  onBack,
}) => {
  return (
    <div>
      {/* ── 2-column layout: [index rail] + [report content] ── */}
      {/* OIRPageHeader removed — Export/Share are in the EATabBar row */}
      <div className="flex gap-6 items-start">

        {/* Left rail — sticky index (desktop) / mobile dropdown */}
        <OIRReportIndex />

        {/* Right content column */}
        <div className="flex-1 min-w-0">

          <Section id="oir-exec-summary">
            <OIRSectionExecutiveSummary
              clientName={clientName}
              portfolioRangeLabel={portfolioRangeLabel}
              shortlistedRangeLabel={shortlistedRangeLabel}
              selectedCount={finalistCount}
              includedCount={includedCount}
              top10={top10}
              selectedOpportunities={selectedOpportunities}
            />
          </Section>

          <Section id="oir-strategic-context">
            <OIRSectionStrategicContext clientName={clientName} />
          </Section>

          <Section id="oir-opportunity-landscape">
            <OIRSectionOpportunityLandscape rankedOpportunities={rankedOpportunities} />
          </Section>

          {/* Financial Impact moved above Strategy Decision Canvas (March 2026) */}
          <Section id="oir-financial-impact">
            <OIRSectionFinancialImpact
              top10={top10}
              selectedOpportunities={selectedOpportunities}
              portfolioRangeLabel={portfolioRangeLabel}
              shortlistedRangeLabel={shortlistedRangeLabel}
            />
          </Section>

          <Section id="oir-strategy-decision-canvas">
            <OIRSectionStrategyDecisionCanvas top10={top10} onNavigate={onNavigate} />
          </Section>

          <Section id="oir-execution-roadmap">
            <OIRSectionExecutionRoadmap
              rankedOpportunities={rankedOpportunities}
              selectedOpportunities={selectedOpportunities}
            />
          </Section>

          {/* ── Footer: Back to Personalize ── */}
          <div className="pt-4 border-t border-[#e5e7eb] mt-2">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-[#717182] hover:text-[#18365E] hover:bg-white rounded-lg transition-all text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Personalize
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};